import { useCallback, useMemo } from "react";
import {
  FormControl,
  FormHelperText,
  InputLabel,
  Select as MuiSelect,
  Stack,
  styled,
} from "@mui/material";
import clsx from "clsx";
import Loader from "../Loader/Loader";
import ClearInput from "components/clear-input/ClearInput";
import {
  selectGroupOptionsRenderer,
  selectOptionsRenderer,
} from "./selectOptionsRenderers";

const InputLabelStyled = styled(InputLabel)({
  position: "inherit",
  marginBottom: "8px",
  marginLeft: "-8px",
  fontSize: "12px",
  fontWeight: 400,
});

const Placeholder = styled("span")(({ theme }) => ({
  color: theme.palette.text.disabled,
}));

const MuiSelectStyled = styled(MuiSelect)(() => ({
  "&.square": {},
  "& .clear-icon": {
    marginRight: "1rem",
    "&.hidden": {
      display: "none",
    },
  },
}));

const SelectNewUnstyled = ({
  id,
  corners = "round", // 'square' | 'round',
  options,
  onChange,
  isError,
  helperText,
  name,
  label,
  renderValue,
  defaultValue = "",
  displayEmpty = true,
  isDisabled,
  isViewMode,
  isClearable,
  isFullWidth = true,
  isMultiple,
  placeholder,
  groups,
  value,
  isLoading,
  itemRenderer,
  className,
  classNames = {}, // label,placeholder,select,helper
}) => {
  const inputId = useMemo(
    () => (id || name) + Math.floor(Math.random() * 1000).toString(),
    [id, name],
  );

  const shouldShowLoader = !isViewMode && isLoading;
  const hasValue = useMemo(() => {
    if (Array.isArray(value)) {
      return value.length > 0;
    }
    return Boolean(value);
  }, [value]);

  const optionsMap = useMemo(() => {
    const newMap = new Map();
    for (const option of options) {
      newMap.set(option.value, option);
    }
    return newMap;
  }, [options]);

  const handleClear = useCallback(() => {
    if (isMultiple) {
      onChange({ target: { value: [], name } });
    } else {
      onChange({ target: { value: "", name } });
    }
  }, [isMultiple, onChange, name]);

  const optionsRenderer = useMemo(() => {
    const shouldDisplayGroups = groups && Array.isArray(groups);

    if (shouldDisplayGroups) {
      return groups.map((group) =>
        selectGroupOptionsRenderer({ group, options, itemRenderer }),
      );
    }
    return options?.map((option) =>
      selectOptionsRenderer({ option, itemRenderer }),
    );
  }, [groups, itemRenderer, options]);

  const checkIfValueFromOptions = useCallback(
    (value) => {
      if (Array.isArray(value)) {
        return value.some((v) => optionsMap.has(v));
      }
      if (Array.isArray(defaultValue)) {
        return defaultValue.some((v) => optionsMap.has(v));
      }
      return optionsMap.has(value) || optionsMap.has(defaultValue);
    },
    [defaultValue, optionsMap],
  );

  const handleRenderValue = useCallback(
    (value) => {
      const isValueFromOptions = checkIfValueFromOptions(value);

      let displayed = "";
      if (!isValueFromOptions) {
        displayed = <Placeholder>{placeholder}</Placeholder>;
      } else if (typeof renderValue === "function") {
        displayed = renderValue(value);
      } else {
        displayed = optionsMap.get(value)?.label || "";
      }

      return (
        <Stack direction="row" alignItems="center" gap={1}>
          {shouldShowLoader && (
            <span>
              <Loader />
            </span>
          )}
          <span className="text-truncate">{displayed}</span>
        </Stack>
      );
    },
    [
      checkIfValueFromOptions,
      optionsMap,
      placeholder,
      renderValue,
      shouldShowLoader,
    ],
  );

  return (
    <FormControl size="small" fullWidth={isFullWidth} className={className}>
      {label ? (
        <InputLabelStyled
          id={inputId}
          shrink={false}
          className={clsx("label", classNames.label)}
        >
          {label}
        </InputLabelStyled>
      ) : (
        // !isValuesFromOptions && (
        //   <Placeholder
        //     id={inputId}
        //     shrink={false}
        //     className={clsx("placeholder", classNames.placeholder)}
        //   >
        //     {shouldShowLoader && <Loader />}
        //     {placeholder}
        //   </Placeholder>
        // )
        <></>
      )}
      <MuiSelectStyled
        renderValue={handleRenderValue}
        value={value}
        displayEmpty={displayEmpty}
        disabled={isViewMode || isDisabled}
        labelId={inputId}
        defaultValue={defaultValue}
        onChange={onChange}
        multiple={isMultiple}
        name={name}
        error={Boolean(isError)}
        endAdornment={
          <ClearInput
            hasValue={hasValue}
            onClear={handleClear}
            className={clsx("clear-icon", { hidden: !isClearable })}
          />
        }
        inputProps={{
          className: clsx("select-input", classNames.input),
        }}
        className={clsx(
          "select",
          corners,
          { "view-mode": isViewMode },
          classNames.select,
        )}
      >
        {optionsRenderer}
      </MuiSelectStyled>
      {helperText && (
        <FormHelperText
          error={Boolean(isError)}
          className={clsx("helper", classNames.helper)}
        >
          {helperText}
        </FormHelperText>
      )}
    </FormControl>
  );
};

const SelectNew = styled(SelectNewUnstyled)({});

export default SelectNew;
